import { Link } from "react-router-dom";
import { Tag, Flame } from "lucide-react";
import Countdown, { useLaunchState } from "@/components/Countdown";

/**
 * LaunchHook — seasonal urgency ribbon anchored to the Black Friday launch date.
 * Auto-hides once the launch has occurred (state.phase === "launched") or the
 * day after launch to avoid stale seasonal copy in prod.
 */
export default function HalloweenHook() {
  const state = useLaunchState();
  if (!state?.launch_at) return null;

  // Hide the hook the day after launch (no longer seasonal).
  const cutoff = new Date("2026-11-28T00:00:00-05:00").getTime();
  if (Date.now() >= cutoff) return null;
  if (state.phase === "launched") return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "var(--ff-accent-kraft)" }}
      data-testid="halloween-hook-section"
      aria-labelledby="halloween-hook-heading"
    >
      {/* Subtle radial spotlight in citrus for warmth */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background: "radial-gradient(circle at 15% 50%, var(--ff-citrus), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 py-10 sm:py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-start gap-4 max-w-2xl">
          <div
            className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center shadow-sm"
            style={{ background: "var(--ff-citrus)", color: "#fff" }}
            aria-hidden="true"
          >
            <Tag size={20} strokeWidth={2.25} />
          </div>
          <div>
            <p
              className="overline"
              style={{ color: "var(--ff-primary)" }}
              data-testid="halloween-hook-overline"
            >
              Launching Black Friday · Nov 27
            </p>
            <h2
              id="halloween-hook-heading"
              className="font-display text-2xl sm:text-3xl mt-2 leading-snug"
              style={{ color: "var(--ff-primary)" }}
              data-testid="halloween-hook-heading"
            >
              Ready in time for your holiday table.
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[var(--ff-text-primary)]/80">
              Our first drop lands on the doorstep for holiday roasts, gatherings worth cooking for, and a freezer stocked with 100% American-raised beef and chicken.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:pl-6">
          <div
            className="rounded-2xl px-5 py-3 bg-white/80 backdrop-blur-sm border border-white/60"
            data-testid="halloween-hook-countdown"
          >
            <span className="overline text-[var(--ff-primary)]">Launch in</span>
            <div className="mt-1" style={{ color: "var(--ff-primary)" }}>
              <Countdown to={state.launch_at} compact />
            </div>
          </div>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap"
            style={{ background: "var(--ff-primary)", color: "#fff" }}
            data-testid="halloween-hook-cta"
          >
            <Flame size={15} /> Secure your seat
          </Link>
        </div>
      </div>
    </section>
  );
}
